import { Flex } from '@chakra-ui/react';
import classNames from 'classnames';
import { useLayoutEffect, useRef } from 'react';

import { TourClasses } from '@revelio/core';

import { usePageState } from '../PageState/PageStateProvider';
import styles from './TabManager.module.css';
import { useGetTabs, useUpdateTab } from './useTabs';

const TAB_WIDTH = 56;
const TAB_GAP = 2;
const TABLIST_PADDING = 2;

// Persists across remounts so the slider can animate from its previous
// position. CustomDash uses `key={tabContent.id}` so changing tab unmounts
// TabManager — without this cache the slider has no "from" value to
// transition from.
let lastSliderLeft: number | null = null;

const computeSliderLeft = (activeIndex: number) =>
  activeIndex >= 0
    ? TABLIST_PADDING + activeIndex * (TAB_WIDTH + TAB_GAP)
    : TABLIST_PADDING;

export const NewLayoutTabManager = () => {
  const tabs = useGetTabs();
  const { pageState } = usePageState();
  const updateTab = useUpdateTab();

  const matchedIndex = tabs.findIndex(
    (tab) => tab.id === pageState.activeTabId
  );
  const activeIndex = matchedIndex >= 0 ? matchedIndex : 0;
  const targetSliderLeft = computeSliderLeft(activeIndex);
  // Render at the cached "from" position so CSS has a starting point to
  // transition from. The useLayoutEffect below imperatively bumps the DOM
  // to the target on the next animation frame, triggering the CSS slide.
  const initialSliderLeft = lastSliderLeft ?? targetSliderLeft;

  const sliderRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    if (lastSliderLeft === targetSliderLeft) return;
    // rAF ensures the browser paints `initialSliderLeft` once before we mutate
    // to the target — without this both transforms flush in one paint and CSS
    // has nothing to animate from. Animating `transform` (not `left`) keeps
    // the slide on the compositor so chart-loading work on the main thread
    // can't drop frames.
    const raf = requestAnimationFrame(() => {
      el.style.transform = `translate3d(${targetSliderLeft}px, 0, 0)`;
      lastSliderLeft = targetSliderLeft;
    });
    return () => cancelAnimationFrame(raf);
  }, [targetSliderLeft]);

  const handleTabChange = (tabId: string) => {
    updateTab(tabId);
  };

  if (tabs.length === 1) return null;

  return (
    <Flex alignItems="center" className={TourClasses.TOUR_VIEW_CLASS}>
      <div role="tablist" className={styles.container}>
        <div
          ref={sliderRef}
          aria-hidden
          className={styles.slider}
          style={{
            transform: `translate3d(${initialSliderLeft}px, 0, 0)`,
          }}
        />
        {tabs.map((tab, i) => {
          const isActive = activeIndex === i;
          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => handleTabChange(tab.id)}
              className={classNames(styles.tab, isActive && styles.tabActive)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </Flex>
  );
};
